import { graphql } from 'gql.tada';
import { client } from '../client';
import { pdxEvent } from '../../utils/noflake';

export const getClaimQuery = graphql(`
  query Claim($input: ClaimInput!) {
    claim(input: $input) {
      id
      cancellable
      status
      packagingOption
      deliveryType
      deliveryMethod
      location {
        url
        storeName2
        storeName
        openingHours {
          hours
          day
        }
        longitude
        logoUrl
        latitude
        id
        distance
        display
        attributes {
          value
          name
        }
        address {
          provinceCode
          province
          postalCode
          lastName
          firstName
          countryCode
          city
          address2
          address1
        }
      }
      submitDate
      shipByDate
      items {
        orderNumber
        id
        name
        quantity
        refundPrice
        image {
          url
        }
        reason {
          title
          code
          retailerReasonCode
          retailerReasonCodeId
          resolutions {
            id
            scope
          }
        }
      }
      shipments {
        trackingNumber
        trackingUrl
        labelUrl
        qrCodeUrl
      }
    }
  }
`);

// Track if we've already found a label for this request to avoid duplicate success events
const labelFoundCache = new Set<string>();

// Function to clear the cache for a specific request (useful when starting a new polling session)
export const clearLabelFoundCache = (requestId: string) => {
  labelFoundCache.delete(requestId);
};

export const getClaimPollingQueryFn = async (requestId: string) => {
  try {
    const result = await client.request(getClaimQuery, {
      input: {
        requestId,
      },
    });

    // Check if we have shipments with labels
    const shipments = result.claim?.shipments || [];
    const hasLabel = shipments.some(
      (shipment) => shipment.labelUrl || shipment.qrCodeUrl,
    );

    if (hasLabel) {
      // Only emit success event if we haven't already found a label for this request
      if (!labelFoundCache.has(requestId)) {
        labelFoundCache.add(requestId);
        pdxEvent({
          event_category: 'get_data',
          event_type: 'get_label_success',
          event_value: 'label',
        });
      }
    } else {
      pdxEvent({
        event_category: 'get_data',
        event_type: 'get_label_polling',
        event_value: 'label',
      });
    }

    return result;
  } catch (error) {
    pdxEvent({
      event_category: 'error',
      event_type: 'get_label_error',
      event_value: 'get_data failed to get label',
    });
    throw error;
  }
};

export const getClaimQueryFn = async (requestId: string) => {
  pdxEvent({
    event_category: 'get_data',
    event_type: 'get_claim',
    event_value: 'claim',
  });

  try {
    const result = await client.request(getClaimQuery, {
      input: {
        requestId,
      },
    });

    pdxEvent({
      event_category: 'get_data',
      event_type: 'get_claim_success',
      event_value: 'claim',
    });

    return result;
  } catch (error) {
    pdxEvent({
      event_category: 'error',
      event_type: 'get_claim_error',
      event_value: 'get_data failed to get claim',
    });
    throw error;
  }
};

export type GetCartResult = Awaited<ReturnType<typeof getClaimQueryFn>>;
export type Claim = GetCartResult['claim'];
export type Shipments = Claim['shipments'];
export type Shipment = Shipments[number];
export type Location = Claim['location'];
